import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, MapPin, Package, Phone, Search, Store, Truck, X } from 'lucide-react'
import {
  formatDateTime, formatMoney, formatNumber, FULFILLMENT_TYPES, ORDER_STATUSES, ORDER_TRANSITIONS,
  PERMISSIONS, type OrderStatus,
} from '@najah/core'
import {
  Alert, Badge, Button, Dialog, EmptyState, Input, Pagination, Textarea,
  Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { ordersApi, type OrderFilter } from '../lib/ordersApi'
import { useAuth } from '../lib/auth'

const STATUS_TONE: Record<OrderStatus, 'success' | 'warning' | 'danger' | 'info' | 'primary' | 'neutral'> = {
  PENDING: 'warning',
  CONFIRMED: 'info',
  PREPARING: 'info',
  READY: 'primary',
  OUT_FOR_DELIVERY: 'info',
  DELIVERED: 'success',
  CANCELLED: 'neutral',
  REJECTED: 'danger',
}

export function OrdersPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.ORDER_MANAGE)

  const [filter, setFilter] = useState<OrderFilter>({ page: 1, pageSize: 25 })
  const [searchInput, setSearchInput] = useState('')
  const [detailId, setDetailId] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['orders', filter],
    queryFn: () => ordersApi.list(filter),
    placeholderData: keepPreviousData,
    // الطلبات تصل من التطبيق أثناء العمل — تحديث دوري يُبقي الشاشة حيّة
    refetchInterval: 60_000,
  })

  const { data: counts } = useQuery({
    queryKey: ['orders', 'counts'],
    queryFn: ordersApi.counts,
    refetchInterval: 60_000,
  })

  const { data: detail } = useQuery({
    queryKey: ['orders', 'detail', detailId],
    queryFn: () => ordersApi.get(detailId!),
    enabled: !!detailId,
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) =>
      ordersApi.updateStatus(id, { status, note: note.trim() }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      void queryClient.invalidateQueries({ queryKey: ['sales'] })
      setNote('')
      toast.success('تحدّثت حالة الطلب', ORDER_STATUSES[res.status])
    },
    onError: (err) => toast.error('تعذّر تغيير الحالة', err instanceof Error ? err.message : undefined),
  })

  function patch(next: OrderFilter) {
    setFilter((f) => ({ ...f, ...next, page: next.page ?? 1 }))
  }

  const pending = counts?.PENDING ?? 0

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">الطلبات</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {data ? `${formatNumber(data.total)} طلب` : 'جارٍ التحميل…'}
          </p>
        </div>
        {pending > 0 && (
          <Badge tone="warning" dot className="text-sm">
            {formatNumber(pending)} بانتظار المراجعة
          </Badge>
        )}
      </div>

      {/* شريط الحالات كمرشّحات سريعة */}
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-none">
        <button
          type="button"
          onClick={() => patch({ status: undefined })}
          className={cn(
            'shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
            !filter.status ? 'border-primary bg-primary text-fg-on-primary' : 'border-border bg-surface text-fg-muted',
          )}
        >
          الكل
        </button>
        {(Object.keys(ORDER_STATUSES) as OrderStatus[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => patch({ status: s })}
            className={cn(
              'shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
              filter.status === s
                ? 'border-primary bg-primary text-fg-on-primary'
                : 'border-border bg-surface text-fg-muted',
            )}
          >
            {ORDER_STATUSES[s]}
            {counts && counts[s] > 0 && (
              <span className="ms-1.5 tabular-nums opacity-70" dir="ltr">{counts[s]}</span>
            )}
          </button>
        ))}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); patch({ search: searchInput.trim() || undefined }) }}
      >
        <Input
          placeholder="رقم الطلب أو اسم الزبون أو هاتفه…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          startIcon={<Search className="size-4" />}
          endIcon={
            searchInput ? (
              <button
                type="button"
                onClick={() => { setSearchInput(''); patch({ search: undefined }) }}
                className="pointer-events-auto rounded p-0.5 text-fg-subtle hover:text-fg"
                aria-label="مسح"
              >
                <X className="size-4" />
              </button>
            ) : undefined
          }
        />
        <Button type="submit" variant="secondary">بحث</Button>
      </form>

      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th>رقم الطلب</Th>
              <Th>الزبون</Th>
              <Th>الاستلام</Th>
              <Th>وقت الطلب</Th>
              <Th align="center">الأصناف</Th>
              <Th align="end">الإجمالي</Th>
              <Th>الحالة</Th>
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={6} cols={7} />
          ) : (
            <Tbody>
              {data?.items.map((o) => (
                <Tr key={o.id} className="cursor-pointer" onClick={() => setDetailId(o.id)}>
                  <Td className="font-mono text-xs font-medium text-primary" dir="ltr">{o.reference}</Td>
                  <Td>
                    <p className="font-medium text-fg">{o.customerName}</p>
                    <p className="flex items-center gap-1 text-xs text-fg-subtle" dir="ltr">
                      <Phone className="size-3" />{o.customerPhone}
                    </p>
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-1.5 text-sm text-fg-muted">
                      {o.fulfillment === 'DELIVERY' ? <Truck className="size-3.5" /> : <Store className="size-3.5" />}
                      {FULFILLMENT_TYPES[o.fulfillment]}
                    </span>
                  </Td>
                  <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(o.placedAt)}</Td>
                  <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(o.itemCount)}</Td>
                  <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(o.total)}</Td>
                  <Td><Badge tone={STATUS_TONE[o.status]} dot>{ORDER_STATUSES[o.status]}</Badge></Td>
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState
            icon={<Package className="size-10" />}
            title="لا توجد طلبات"
            description="الطلبات القادمة من تطبيق الزبائن تظهر هنا"
          />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            pageSize={data.pageSize}
            onPageChange={(page) => setFilter((f) => ({ ...f, page }))}
            onPageSizeChange={(pageSize) => patch({ pageSize })}
          />
        )}
      </TableWrapper>

      {/* ── تفاصيل الطلب وإدارة حالته ── */}
      <Dialog
        open={!!detailId}
        onClose={() => { setDetailId(null); setNote('') }}
        size="lg"
        title={detail?.reference}
        description={detail ? `${detail.customerName} · ${detail.customerPhone}` : undefined}
      >
        {detail && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONE[detail.status]} dot>{ORDER_STATUSES[detail.status]}</Badge>
              <Badge tone="neutral">{FULFILLMENT_TYPES[detail.fulfillment]}</Badge>
              <span className="text-xs text-fg-subtle">
                <Clock className="me-1 inline size-3" />
                {formatDateTime(detail.placedAt)}
              </span>
            </div>

            {detail.addressSnapshot && (
              <div className="flex gap-2 rounded-lg bg-bg-subtle p-3 text-sm">
                <MapPin className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
                <span className="text-fg">{detail.addressSnapshot}</span>
              </div>
            )}

            {detail.customerNote && (
              <Alert tone="info" title="ملاحظة الزبون">{detail.customerNote}</Alert>
            )}

            <TableWrapper>
              <Table>
                <Thead>
                  <tr>
                    <Th>الصنف</Th>
                    <Th align="center">المطلوب</Th>
                    <Th align="center">المتوفّر</Th>
                    <Th align="end">الإجمالي</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {detail.items.map((item) => {
                    const short = item.available < item.quantity
                    return (
                      <Tr key={item.id}>
                        <Td>
                          <p className="font-medium text-fg">{item.nameSnapshot}</p>
                          <p className="font-mono text-xs text-fg-subtle" dir="ltr">{item.skuSnapshot}</p>
                        </Td>
                        <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(item.quantity)}</Td>
                        <Td align="center">
                          <span
                            className={cn('font-semibold tabular-nums', short ? 'text-danger' : 'text-success')}
                            dir="ltr"
                          >
                            {formatNumber(item.available)}
                          </span>
                        </Td>
                        <Td align="end" className="font-semibold tabular-nums" dir="ltr">
                          {formatMoney(item.lineTotal)}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </TableWrapper>

            {detail.items.some((i) => i.available < i.quantity) && (
              <Alert tone="warning" title="نقص في المخزون">
                بعض الأصناف رصيدها أقلّ من المطلوب — راجعها قبل تأكيد الطلب.
              </Alert>
            )}

            <div className="space-y-1 border-t border-border pt-3 text-sm">
              <div className="flex justify-between text-fg-muted">
                <span>المجموع</span>
                <span dir="ltr">{formatMoney(detail.subtotal)}</span>
              </div>
              {detail.deliveryFee > 0 && (
                <div className="flex justify-between text-fg-muted">
                  <span>التوصيل</span>
                  <span dir="ltr">{formatMoney(detail.deliveryFee)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-2 text-lg font-bold text-fg">
                <span>الإجمالي</span>
                <span dir="ltr">{formatMoney(detail.total)}</span>
              </div>
            </div>

            {/* الانتقالات المسموحة فقط — الجدول في @najah/core هو المرجع */}
            {canManage && ORDER_TRANSITIONS[detail.status].length > 0 && (
              <div className="space-y-3 border-t border-border pt-4">
                <Textarea
                  label="ملاحظة (تُعرَض للزبون)" rows={2}
                  value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="سبب الرفض، وقت الجهوزية…"
                />
                <div className="flex flex-wrap gap-2">
                  {ORDER_TRANSITIONS[detail.status].map((next) => (
                    <Button
                      key={next}
                      variant={next === 'REJECTED' || next === 'CANCELLED' ? 'danger' : 'primary'}
                      onClick={() => statusMutation.mutate({ id: detail.id, status: next })}
                      loading={statusMutation.isPending && statusMutation.variables?.status === next}
                    >
                      {ORDER_STATUSES[next]}
                    </Button>
                  ))}
                </div>
                {detail.status === 'OUT_FOR_DELIVERY' || detail.status === 'READY' ? (
                  <p className="text-xs text-fg-subtle">
                    «تمّ التسليم» يُنشئ فاتورة بيع ويُخرج البضاعة من المخزون.
                  </p>
                ) : null}
              </div>
            )}

            {detail.history.length > 0 && (
              <div className="border-t border-border pt-4">
                <h3 className="mb-2 text-sm font-semibold text-fg-muted">سجل الحالات</h3>
                <ul className="space-y-1.5">
                  {detail.history.map((h, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-xs">
                      <Badge tone={STATUS_TONE[h.status]}>{ORDER_STATUSES[h.status]}</Badge>
                      <span className="flex-1 truncate text-fg-subtle">{h.note ?? ''}</span>
                      <span className="text-fg-subtle">{h.userName ?? 'الزبون'}</span>
                      <span className="whitespace-nowrap text-fg-subtle">{formatDateTime(h.at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  )
}
